'use client';

/**
 * API Client
 * Handles all backend API communication with proper authentication and error handling
 * Uses axios for all HTTP requests
 */

import axios, {
  AxiosError,
  AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiTransport, ApiTransportRequest } from '@cabal/shared/api-client/types';
import { resolveBackendUrl } from '@/config/backend-url';
import { ensureFreshAccessToken } from '@/lib/authTokenFreshness';
import { reportApiAuthSuccess, reportApiUnauthorized } from '@/lib/authExpiryGuard';
import { handleAxiosError } from '@/lib/apiClientErrors';
import { installRetryInterceptor } from '@/lib/apiClientRetry';
import { createTransportRequestConfig } from '@/lib/apiClientTransport';
import {
  GalleryJankHypothesis,
  galleryJankLog,
} from '@/debug/gallery-scroll-jank-seq';
import type { ResponseEnvelope, ErrorResponse } from '@/types/frontend-types';

export {
  ApiError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
} from '@/lib/apiClientErrors';
export { NO_RETRY_HEADER } from '@/lib/apiClientTransport';

// ============================================================================
// Configuration
// ============================================================================

const BACKEND_URL = resolveBackendUrl();

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get authentication headers for API requests.
 *
 * The bearer token is resolved through the freshness precondition, so a request
 * never carries a token that is already expired or about to be — an expired
 * token is refreshed here rather than discovered by the 401 breaker downstream.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await ensureFreshAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

// ============================================================================
// Axios Instance with Retry Logic
// ============================================================================

const HTTP_STATUS_UNAUTHORIZED = 401;

const AUTHORIZATION_HEADER = 'Authorization';

/**
 * Did this request actually present a bearer token?
 *
 * A 401 on a request that carried no credential says nothing about the
 * credential — it is the expected answer for a signed-out visitor hitting a
 * private route. Feeding those to the expiry breaker would let ordinary
 * signed-out browsing trip it and drive a forced sign-out of someone who was
 * never signed in.
 */
function didRequestCarryCredential(
  config: InternalAxiosRequestConfig | undefined,
): boolean {
  const headers = config?.headers;
  if (!headers) return false;
  if (typeof headers.has === 'function') {
    return headers.has(AUTHORIZATION_HEADER);
  }
  return Object.keys(headers).some(
    (name) =>
      name.toLowerCase() === AUTHORIZATION_HEADER.toLowerCase() &&
      Boolean(headers[name]),
  );
}

const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000, // 30 seconds
});

// --- TEMPORARY gallery-scroll-jank instrumentation (H1: mid-scroll parse) ---
// Wraps axios's default JSON transform to measure how long the main thread
// spends parsing each response body, and logs it with the URL and raw size.
// Dev-only by construction: the Seq transport is a no-op outside dev.
const defaultTransforms = axios.defaults.transformResponse;
const defaultTransformList = Array.isArray(defaultTransforms)
  ? defaultTransforms
  : [defaultTransforms].filter((transform) => transform != null);
axiosInstance.defaults.transformResponse = defaultTransformList.map(
  (transform) =>
    function timedTransform(
      this: unknown,
      data: unknown,
      headers: unknown,
      status?: number,
    ) {
      const rawChars = typeof data === 'string' ? data.length : null;
      const startedAtMs = performance.now();
      const parsed = (
        transform as (this: unknown, d: unknown, h: unknown, s?: number) => unknown
      ).call(this, data, headers, status);
      const parseMs = performance.now() - startedAtMs;
      const config = this as { url?: string } | undefined;
      galleryJankLog(
        'axios-transform-response',
        GalleryJankHypothesis.Network,
        {
          url: config?.url ?? '(unknown)',
          status: status ?? null,
          rawChars,
          parseMs: Math.round(parseMs * 100) / 100,
        },
      );
      return parsed;
    },
);
// --- END TEMPORARY instrumentation ---

// Feed the auth-expiry breaker (consecutive-401 streak → suspend polling +
// force refresh). Registered BEFORE the retry interceptor so it observes the
// raw per-attempt outcome; 401 is a terminal status for the retry logic, so
// each rejected request reports exactly once.
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    reportApiAuthSuccess();
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === HTTP_STATUS_UNAUTHORIZED) {
      // Only a 401 against a presented credential is evidence about that
      // credential — see `didRequestCarryCredential`.
      if (didRequestCarryCredential(error.config)) {
        reportApiUnauthorized({
          method: error.config?.method,
          url: error.config?.url,
        });
      }
    } else if (error.response) {
      // Any non-401 response means the credential passed auth.
      reportApiAuthSuccess();
    }
    return Promise.reject(error);
  },
);

installRetryInterceptor({ axiosInstance });

// ============================================================================
// Typed API Wrapper Transport Adapter
// ============================================================================

/**
 * Transport adapter for generated API wrapper calls.
 * Keeps existing axios retry and error behavior while delegating auth headers to ApiWrapper.
 */
export function createApiTransport(): ApiTransport {
  return {
    request: async <
      TOutput,
      B = undefined,
      Q extends object | undefined = undefined,
    >({
      method,
      url,
      headers,
      query,
      body,
      signal,
    }: ApiTransportRequest<B, Q>): Promise<TOutput> => {
      try {
        const requestConfig = createTransportRequestConfig({
          method,
          url,
          headers,
          query,
          body,
          signal,
        });
        const response = await axiosInstance.request<TOutput>(requestConfig);
        return response.data;
      } catch (error) {
        handleAxiosError(error as AxiosError<ErrorResponse>);
      }
    },
  };
}

// ============================================================================
// Main API Client
// ============================================================================

/**
 * Make GET request to API with query parameters
 */
export async function apiGet<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  try {
    const headers = await getAuthHeaders();
    const response: AxiosResponse<T> = await axiosInstance.get(endpoint, {
      headers,
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError<ErrorResponse>);
  }
}

/**
 * Make GET request that returns response envelope
 */
export async function apiGetEnvelope<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
): Promise<ResponseEnvelope<T>> {
  return apiGet<ResponseEnvelope<T>>(endpoint, params);
}

/**
 * Make POST request to API
 */
export async function apiPost<T>(
  endpoint: string,
  data?: unknown,
): Promise<T> {
  try {
    const isFormData = data instanceof FormData;
    const headers = await getAuthHeaders();
    
    // For FormData, remove Content-Type to let axios set it with boundary
    if (isFormData) {
      delete headers['Content-Type'];
    }

    const response: AxiosResponse<T> = await axiosInstance.post(endpoint, data, {
      headers,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError<ErrorResponse>);
  }
}

/**
 * Make PATCH request to API
 */
export async function apiPatch<T>(
  endpoint: string,
  data?: unknown,
): Promise<T> {
  try {
    const headers = await getAuthHeaders();
    const response: AxiosResponse<T> = await axiosInstance.patch(endpoint, data, {
      headers,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError<ErrorResponse>);
  }
}

/**
 * Make PUT request to API
 */
export async function apiPut<T>(
  endpoint: string,
  data?: unknown,
): Promise<T> {
  try {
    const headers = await getAuthHeaders();
    const response: AxiosResponse<T> = await axiosInstance.put(endpoint, data, {
      headers,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError<ErrorResponse>);
  }
}

/**
 * Make DELETE request to API
 */
export async function apiDelete<T>(
  endpoint: string,
): Promise<T> {
  try {
    const headers = await getAuthHeaders();
    const response: AxiosResponse<T> = await axiosInstance.delete(endpoint, {
      headers,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError<ErrorResponse>);
  }
}
